// GeoLink fetchers — staff-realm reads backing the Nearby Bookings and
// Coverage Analysis pages. Both endpoints return the standard
// success-envelope and we unwrap to the `data` payload here.

import { apiClient } from './client';
import { endpoints } from './endpoints';

// -- Nearby --------------------------------------------------------------

export interface NearbyBookingDto {
  enc_id: string;
  booking_number: string;
  date: string;
  timeframe: string;
  postcode: string;
  customer_name: string;
  customer_phone: string;
  address: string;
  price: number | null;
  payment_mode: string | null;
  status: string;
  driver_enc_id: string | null;
  driver_name: string | null;
  latitude: number;
  longitude: number;
  distance_miles: number;
}

export interface NearbySkipJobDto {
  enc_id: string;
  job_number: string;
  date: string | null;
  timeframe: string | null;
  postcode: string;
  customer_name: string;
  customer_phone: string;
  address: string | null;
  status: string;
  driver_enc_id: string | null;
  driver_name: string | null;
  latitude: number;
  longitude: number;
  distance_miles: number;
}

export interface NearbyResponse {
  centre: {
    latitude: number;
    longitude: number;
    postcode: string;
  };
  radius_miles: number;
  date_from: string;
  date_to: string;
  bookings: NearbyBookingDto[];
  skip_jobs: NearbySkipJobDto[];
}

export interface GetNearbyParams {
  postcode: string;
  radius_miles?: number;
  date_from?: string;
  date_to?: string;
  driver_enc_id?: string;
  include_bookings?: boolean;
  include_skip_jobs?: boolean;
}

export async function getNearbyResults(
  params: GetNearbyParams,
): Promise<NearbyResponse> {
  const search = new URLSearchParams();
  search.set('postcode', params.postcode);
  if (params.radius_miles !== undefined) search.set('radius_miles', String(params.radius_miles));
  if (params.date_from) search.set('date_from', params.date_from);
  if (params.date_to) search.set('date_to', params.date_to);
  if (params.driver_enc_id) search.set('driver_enc_id', params.driver_enc_id);
  if (params.include_bookings !== undefined) {
    search.set('include_bookings', String(params.include_bookings));
  }
  if (params.include_skip_jobs !== undefined) {
    search.set('include_skip_jobs', String(params.include_skip_jobs));
  }
  const res = await apiClient.get<NearbyResponse>(
    `${endpoints.geolink.nearby}?${search.toString()}`,
  );
  return res.data;
}

// -- Coverage ------------------------------------------------------------

export type CoverageSource = 'bookings' | 'skip_jobs' | 'both';

export interface CoveragePointDto {
  outcode: string;
  latitude: number | null;
  longitude: number | null;
  job_count: number;
  total_revenue: number;
  avg_price: number;
  source: CoverageSource;
}

export interface TopAreaDto {
  outcode: string;
  job_count: number;
  total_revenue: number;
  source: CoverageSource;
}

export interface OpportunityDto {
  outcode: string;
  job_count: number;
  source: CoverageSource;
}

export interface CoverageResponse {
  coverage_points: CoveragePointDto[];
  top_areas: TopAreaDto[];
  opportunities: OpportunityDto[];
  period_days: number;
  generated_at: string;
}

export interface GetCoverageParams {
  period_days?: 30 | 60 | 90 | 180 | 365;
  service?: string;
  density?: 1 | 6 | 16;
  include_bookings?: boolean;
  include_skip_jobs?: boolean;
}

export async function getCoverageData(
  params: GetCoverageParams,
): Promise<CoverageResponse> {
  const search = new URLSearchParams();
  if (params.period_days !== undefined) search.set('period_days', String(params.period_days));
  if (params.service) search.set('service', params.service);
  if (params.density !== undefined) search.set('density', String(params.density));
  if (params.include_bookings !== undefined) {
    search.set('include_bookings', String(params.include_bookings));
  }
  if (params.include_skip_jobs !== undefined) {
    search.set('include_skip_jobs', String(params.include_skip_jobs));
  }
  const query = search.toString();
  const url = query.length > 0
    ? `${endpoints.geolink.coverage}?${query}`
    : endpoints.geolink.coverage;
  const res = await apiClient.get<CoverageResponse>(url);
  return res.data;
}
